import { z } from "zod";
import { SearchFiltersSchema } from "./search.js";

export const SavedSearchTypeEnum = z.enum(["keyword", "semantic", "agentic"]);

export type SavedSearchType = z.infer<typeof SavedSearchTypeEnum>;

/**
 * The query payload for a saved search. All modes share a free-text ``q``;
 * semantic searches may opt into reranking. The shape is intentionally a
 * superset so a single column (``saved_search.query``) round-trips for every
 * mode. The runner reads only the fields relevant to the search ``type``.
 */
export const SavedSearchQuerySchema = z
  .object({
    q: z.string().min(1).max(1000),
    rerank: z.boolean().optional(),
  })
  .strict();

export type SavedSearchQuery = z.infer<typeof SavedSearchQuerySchema>;

export const SavedSearchSchema = z.object({
  id: z.string().uuid(),
  ownerId: z.string().uuid(),
  orgId: z.string().uuid(),
  type: SavedSearchTypeEnum,
  query: z.record(z.string(), z.unknown()),
  filters: z.record(z.string(), z.unknown()).optional().nullable(),
  schedule: z.string().optional().nullable(),
  notificationPrefs: z.record(z.string(), z.unknown()).optional().nullable(),
  /** Suppress re-notifying about the same document within this window. */
  dedupWindowSeconds: z.number().int().nonnegative(),
  lastSeenSet: z.array(z.string()),
  lastRunAt: z.string().datetime().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type SavedSearch = z.infer<typeof SavedSearchSchema>;

/** Notification-channel preferences for a saved search. */
export const SavedSearchNotificationPrefsSchema = z
  .object({
    channels: z.array(z.enum(["inapp", "email", "webhook"])).default(["inapp"]),
  })
  .strict();

export type SavedSearchNotificationPrefs = z.infer<typeof SavedSearchNotificationPrefsSchema>;

/** Default dedup window: 24 hours. */
export const DEFAULT_DEDUP_WINDOW_SECONDS = 86_400;

/** ``POST /saved-searches`` request body. */
export const CreateSavedSearchRequestSchema = z
  .object({
    type: SavedSearchTypeEnum,
    query: SavedSearchQuerySchema,
    filters: SearchFiltersSchema.optional(),
    schedule: z.string().min(1).max(120).optional(),
    notificationPrefs: SavedSearchNotificationPrefsSchema.optional(),
    dedupWindowSeconds: z
      .number()
      .int()
      .nonnegative()
      .max(31_536_000)
      .default(DEFAULT_DEDUP_WINDOW_SECONDS),
  })
  .strict();

export type CreateSavedSearchRequest = z.infer<typeof CreateSavedSearchRequestSchema>;

/** ``PATCH /saved-searches/:id`` request body — every field optional. */
export const UpdateSavedSearchRequestSchema = z
  .object({
    query: SavedSearchQuerySchema.optional(),
    filters: SearchFiltersSchema.nullable().optional(),
    schedule: z.string().min(1).max(120).nullable().optional(),
    notificationPrefs: SavedSearchNotificationPrefsSchema.nullable().optional(),
    dedupWindowSeconds: z.number().int().nonnegative().max(31_536_000).optional(),
  })
  .strict()
  .refine((value) => Object.keys(value).length > 0, {
    message: "At least one field must be supplied.",
  });

export type UpdateSavedSearchRequest = z.infer<typeof UpdateSavedSearchRequestSchema>;

export const SavedSearchRunStatusEnum = z.enum(["pending", "running", "completed", "failed"]);

export const SavedSearchRunSchema = z.object({
  id: z.string().uuid(),
  savedSearchId: z.string().uuid(),
  mode: SavedSearchTypeEnum,
  ranAt: z.string().datetime(),
  hits: z.number().int().nonnegative(),
  newHits: z.number().int().nonnegative(),
  status: SavedSearchRunStatusEnum,
  elapsedMs: z.number().nonnegative(),
  costUsd: z.number().nonnegative().nullable(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});

export type SavedSearchRun = z.infer<typeof SavedSearchRunSchema>;

/** ``POST /saved-searches/:id/run`` response. */
export const RunSavedSearchResponseSchema = z.object({
  runId: z.string().uuid(),
  savedSearchId: z.string().uuid(),
  mode: SavedSearchTypeEnum,
  status: SavedSearchRunStatusEnum,
  hits: z.number().int().nonnegative(),
  newHits: z.number().int().nonnegative(),
  /** Chunk content-hashes that are new since the previous run. */
  newChunkIds: z.array(z.string()),
  /** Number of notifications enqueued (post-dedup). */
  notificationsEnqueued: z.number().int().nonnegative(),
  elapsedMs: z.number().nonnegative(),
  costUsd: z.number().nonnegative().nullable(),
});

export type RunSavedSearchResponse = z.infer<typeof RunSavedSearchResponseSchema>;

/** ``GET /saved-searches`` response. */
export const ListSavedSearchesResponseSchema = z.object({
  savedSearches: z.array(SavedSearchSchema),
});

export type ListSavedSearchesResponse = z.infer<typeof ListSavedSearchesResponseSchema>;
